// ============================================
// Generic-to-Formulary Mapping Editor
//
// Admin table for mapping Dr. C's generic
// supplement names to HemoLogix formulary product IDs.
// ============================================

import { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { GenericFormularyMapping } from '../../../types/labUpload';
import {
  getAllMappings,
  saveMapping,
  deleteMapping,
} from '../../../services/firestore/genericFormularyService';

export function FormularyMappingEditor() {
  const [mappings, setMappings] = useState<GenericFormularyMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [newGeneric, setNewGeneric] = useState('');
  const [newProductId, setNewProductId] = useState('');
  const [newConfidence, setNewConfidence] = useState<'exact' | 'close' | 'approximate'>('exact');
  const [error, setError] = useState<string | null>(null);

  async function loadMappings() {
    setLoading(true);
    try {
      const all = await getAllMappings();
      setMappings(all);
    } catch (err) {
      console.error('[FormularyMappingEditor] Load failed:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadMappings(); }, []);

  async function handleAdd() {
    if (!newGeneric.trim() || !newProductId.trim()) return;
    setError(null);

    try {
      const mapping: GenericFormularyMapping = {
        mappingId: `map-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        genericName: newGeneric.trim(),
        formularyProductId: newProductId.trim(),
        confidence: newConfidence,
      };

      await saveMapping(mapping);
      setNewGeneric('');
      setNewProductId('');
      setNewConfidence('exact');
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add mapping.');
    }
  }

  async function handleDelete(mappingId: string) {
    try {
      await deleteMapping(mappingId);
      await loadMappings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete mapping.');
    }
  }

  if (loading) {
    return <p className="text-sm text-zinc-500 py-4">Loading mappings...</p>;
  }

  return (
    <div>
      <h4 className="text-[13px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">
        Generic → Formulary Mappings ({mappings.length})
      </h4>

      {/* Existing mappings table */}
      {mappings.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-zinc-900 text-white text-[13px]">
                <th className="py-2 px-3 font-medium">Generic Name</th>
                <th className="py-2 px-3 font-medium">Formulary Product ID</th>
                <th className="py-2 px-3 font-medium">Confidence</th>
                <th className="py-2 px-3 font-medium w-16"></th>
              </tr>
            </thead>
            <tbody>
              {mappings.map((m, i) => (
                <tr key={m.mappingId} className={i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                  <td className="py-2 px-3 text-zinc-800">{m.genericName}</td>
                  <td className="py-2 px-3 font-mono text-[13px] text-zinc-600">{m.formularyProductId}</td>
                  <td className="py-2 px-3">
                    <span className={`text-[13px] px-1.5 py-0.5 rounded ${
                      m.confidence === 'exact' ? 'bg-green-100 text-green-700' :
                      m.confidence === 'close' ? 'bg-amber-100 text-amber-700' :
                      'bg-zinc-100 text-zinc-600'
                    }`}>
                      {m.confidence}
                    </span>
                  </td>
                  <td className="py-2 px-3">
                    <button
                      onClick={() => handleDelete(m.mappingId)}
                      className="text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add new mapping */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label className="text-[13px] text-zinc-500 block mb-1">Generic Name</label>
          <input
            value={newGeneric}
            onChange={(e) => setNewGeneric(e.target.value)}
            placeholder="e.g. Ashwagandha 600mg"
            className="w-full border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
          />
        </div>
        <div className="flex-1">
          <label className="text-[13px] text-zinc-500 block mb-1">Formulary Product ID</label>
          <input
            value={newProductId}
            onChange={(e) => setNewProductId(e.target.value)}
            placeholder="e.g. supp-ashwagandha-ksm66"
            className="w-full border border-zinc-200 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-rose-500"
          />
        </div>
        <div className="w-32">
          <label className="text-[13px] text-zinc-500 block mb-1">Confidence</label>
          <select
            value={newConfidence}
            onChange={(e) => setNewConfidence(e.target.value as 'exact' | 'close' | 'approximate')}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500"
          >
            <option value="exact">Exact</option>
            <option value="close">Close</option>
            <option value="approximate">Approximate</option>
          </select>
        </div>
        <button
          onClick={handleAdd}
          disabled={!newGeneric.trim() || !newProductId.trim()}
          className="px-4 py-2 bg-rose-600 text-white rounded text-sm font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <Plus size={14} />
          Add
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
